import type { IURN } from '@lucimark/shared/models/resource'
import { ResourceType } from '@lucimark/shared/models/resource'

export class URNValidationError extends Error {
  constructor(raw: string) {
    super(`${raw} is not a valid Lucimark URN`)
    this.name = 'URNValidationError' // (2)
  }
}

export class InvalidTenantIdError extends Error {
  constructor(raw: string) {
    super(`${raw} is not a valid tenantId - should be a UUID v4`)
    this.name = 'InvalidTenantIdError' // (2)
  }
}

const URN_SCHEME_PREFIX = 'urn:lucimark:'

const RESOURCE_TYPES = new Set<string>(Object.values(ResourceType))

/**
 * Parse `urn:lucimark:<type>:<tenantId>:<id>[:<variant>]`.
 * `tenantId` and `id` may contain hyphens (UUIDs, `player-master-*`, content hashes).
 */
const decodeURN = (raw: string): IURN => {
  const normalized = raw.trim().toLowerCase()
  if (!normalized.startsWith(URN_SCHEME_PREFIX)) {
    throw new URNValidationError(raw)
  }
  const segments = normalized.slice(URN_SCHEME_PREFIX.length).split(':')
  if (segments.length < 3) {
    throw new URNValidationError(raw)
  }
  const [type, tenantId, id, ...variantParts] = segments
  if (!type || !tenantId || !id || !RESOURCE_TYPES.has(type)) {
    throw new URNValidationError(raw)
  }
  const urn: IURN = {
    type: type as ResourceType,
    tenantId,
    id
  }
  if (variantParts.length > 0) {
    urn.variant = variantParts.join(':')
  }
  return urn
}

const encodeURN = (urn: IURN): string => {
  const { type, tenantId, id, variant } = urn
  return `urn:lucimark:${type}:${tenantId}:${id}${variant ? `:${variant}` : ''}`.toLowerCase()
}

const getURNPrefix = (params: { type: ResourceType, tenantId: string }): string => {
  const { type, tenantId } = params
  return `urn:lucimark:${type}:${tenantId}`.toLowerCase()
}

/** Gallery thumbnail URN for a base asset URN (`…:thumbnail` variant). */
const getAssetThumbnailUrn = (baseAssetUrn: string): string => {
  const d = decodeURN(baseAssetUrn)
  if (d.type !== ResourceType.ASSET) {
    throw new URNValidationError(baseAssetUrn)
  }
  return encodeURN({ type: ResourceType.ASSET, tenantId: d.tenantId, id: d.id, variant: 'thumbnail' })
}

export { decodeURN, encodeURN, getAssetThumbnailUrn, getURNPrefix }
